import { useState, useEffect } from 'react';
import VideoPlayer from './components/VideoPlayer';
import Catalog from './components/Catalog';
import NewEditPopup from './components/NewEditPopup';
import SettingsPopup from './components/SettingsPopup';
import PasswordPopup from './components/PasswordPopup';

function App() {
  const version = 'v4.2.0';
  const [currentView, setCurrentView] = useState('catalog');
  const [currentVideo, setCurrentVideo] = useState({ url: null, cover: null });
  const [timeLeft, setTimeLeft] = useState(45 * 60);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [userType, setUserType] = useState('');
  
  const [filterMode, setFilterMode] = useState(() => localStorage.getItem('filterMode') || 'v');
  const [languageMode, setLanguageMode] = useState(() => localStorage.getItem('languageMode') || 't');
  const [debugMode, setDebugMode] = useState(() => localStorage.getItem('debugMode') === 'true');
  
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [videoPopupMode, setVideoPopupMode] = useState('new');
  const [videoPopupData, setVideoPopupData] = useState(null);
  
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);

  const checkForNewDayReset = () => {
    const savedTimeLimit = localStorage.getItem('timeLimit');
    const lastResetDate = localStorage.getItem('lastResetDate');
    const savedTimeLeft = localStorage.getItem('timeLeft');
    const savedUserType = localStorage.getItem('userType');
    
    const today = new Date().toDateString();
    
    if (lastResetDate !== today) {
      const newTimeLimit = savedTimeLimit ? parseInt(savedTimeLimit) * 60 : 30 * 60;
      setTimeLeft(newTimeLimit);
      localStorage.setItem('lastResetDate', today);
      localStorage.setItem('timeLeft', newTimeLimit.toString());
    } else if (savedTimeLeft) {
      setTimeLeft(parseInt(savedTimeLeft));
    }

    if (savedUserType) {
      setUserType(savedUserType);
    }
  };

  useEffect(() => {
    checkForNewDayReset();
    const interval = setInterval(checkForNewDayReset, 3600000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForNewDayReset();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (isTimerRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          localStorage.setItem('timeLeft', newTime.toString());
          return newTime;
        });
      }, 1000);
    } else if (timeLeft <= 0) {
      setIsTimerRunning(false);
      localStorage.setItem('timeLeft', '0');
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeLeft]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'filterMode') {
        const newValue = localStorage.getItem('filterMode') || 'v';
        if (newValue !== filterMode) {
          setFilterMode(newValue);
        }
      } else if (e.key === 'languageMode') {
        const newValue = localStorage.getItem('languageMode') || 't';
        if (newValue !== languageMode) {
          setLanguageMode(newValue);
        }
      } else if (e.key === 'debugMode') {
        const newValue = localStorage.getItem('debugMode') === 'true';
        if (newValue !== debugMode) {
          setDebugMode(newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    const handleCustomStorageChange = () => {
      const newFilterMode = localStorage.getItem('filterMode') || 'v';
      const newLanguageMode = localStorage.getItem('languageMode') || 't';
      const newDebugMode = localStorage.getItem('debugMode') === 'true';
      
      if (newFilterMode !== filterMode) setFilterMode(newFilterMode);
      if (newLanguageMode !== languageMode) setLanguageMode(newLanguageMode);
      if (newDebugMode !== debugMode) setDebugMode(newDebugMode);
    };
    
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, [filterMode, languageMode, debugMode]);

  const handleVideoSelect = (videoUrl, videoCover) => {
    if (timeLeft > 0) {
      setCurrentVideo({ url: videoUrl, cover: videoCover });
      setCurrentView('player');
    }
  };

  const handleBackToCatalog = () => {
    setCurrentView('catalog');
    setIsTimerRunning(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const notifyCatalogUpdate = () => {
    console.log('📢 App.js: Disparando evento localStorageChange');
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: {
        timestamp: Date.now(),
        source: 'config'
      }
    }));
  };

  const openNewVideoPopup = () => {
    console.log('🎬 Abriendo popup para nuevo video');
    setVideoPopupMode('new');
    setVideoPopupData(null);
    setShowVideoPopup(true);
  };

  const openEditVideoPopup = (videoData) => {
    console.log('✏️ Abriendo popup para editar video:', videoData?.id);
    setVideoPopupMode('edit');
    setVideoPopupData(videoData);
    setShowVideoPopup(true);
  };

  const closeVideoPopup = () => {
    console.log('❌ Cerrando popup de video');
    setShowVideoPopup(false);
    setVideoPopupData(null);
  };

  const handleVideoSaved = () => {
    console.log('✅ Video guardado exitosamente');
    notifyCatalogUpdate();
    closeVideoPopup();
  };

  const handleSettingsChange = (settings) => {
    console.log('⚙️ Configuración actualizada:', settings);
    setFilterMode(settings.filterMode);
    setLanguageMode(settings.languageMode);
    setDebugMode(settings.debugMode);
    
    // Actualizar tiempo si viene en settings
    if (settings.timeMinutes && settings.timeMinutes > 0) {
      const newTimeInSeconds = settings.timeMinutes * 60;
      setTimeLeft(newTimeInSeconds);
      // El localStorage ya lo guarda SettingsPopup
    }
  };

  const handleOpenSettings = () => {
    setShowPasswordPopup(true);
  };

  const handlePasswordSuccess = () => {
    setShowPasswordPopup(false);
    setShowSettingsPopup(true);
  };

  const timerColor = timeLeft <= 5 * 60 ? 'text-red-500' : 'text-white';

  const getFilterColor = (mode) => {
    switch (mode) {
      case 'v': return 'bg-blue-500 text-white';
      case 'm': return 'bg-orange-500 text-white';
      case 'r': return 'bg-purple-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  const getFilterText = (mode) => {
    switch (mode) {
      case 'v': return 'VIDEOS';
      case 'm': return 'MÚSICA';
      case 'r': return 'RADIO';
      default: return 'VIDEOS';
    }
  };

  const getLanguageColor = (mode) => {
    switch (mode) {
      case 'es': return 'bg-green-500 text-white';
      case 'en': return 'bg-red-500 text-white';
      case 't': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getLanguageText = (mode) => {
    switch (mode) {
      case 'es': return 'ES';
      case 'en': return 'EN';
      case 't': return 'TODOS';
      default: return 'TODOS';
    }
  };

  const getDebugColor = () => {
    return debugMode ? 'bg-yellow-500 text-black' : '';
  };

  return (
    <div className="w-full h-full bg-black relative">
      {/* Botón de configuración (engranaje visible) */}
      <button 
        onClick={handleOpenSettings}
        className="absolute top-[10px] left-[10px] bg-black bg-opacity-70 hover:bg-opacity-100 text-white z-[60] w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all"
      >
        <i className="fa-solid fa-gear text-xl"></i>
      </button>

      <div className={`absolute top-[10px] left-1/2 transform -translate-x-1/2 ${timerColor} text-lg font-bold bg-black bg-opacity-70 px-4 py-1 rounded-full z-50 flex items-center gap-2`}>
        <i className="fa-solid fa-clock"></i>
        <span>{formatTime(timeLeft)}</span>
        <span className={`${getLanguageColor(languageMode)} px-2 py-1 rounded text-xs font-bold`}>
          {getLanguageText(languageMode)}
        </span>
        <span className={`${getFilterColor(filterMode)} px-2 py-1 rounded text-xs font-bold`}>
          {getFilterText(filterMode)}
        </span>
        {debugMode && (
          <span className={`${getDebugColor()} px-2 py-1 rounded text-xs font-bold animate-pulse`}>
            DEBUG
          </span>
        )}
      </div>

      <div className='absolute top-[10px] right-[30px] text-white text-sm'>
        v{version}
      </div>

      {currentView === 'catalog' ? (
        <Catalog 
          onVideoSelect={handleVideoSelect} 
          userType={userType}
          onEditVideo={openEditVideoPopup}
        />
      ) : (
        <VideoPlayer 
          url={currentVideo.url}
          videoCover={currentVideo.cover}
          onBack={handleBackToCatalog}
          timeLeft={timeLeft}
          setIsTimerRunning={setIsTimerRunning}
          filterMode={filterMode}
        />
      )}

      {showVideoPopup && (
        <NewEditPopup
          mode={videoPopupMode}
          videoData={videoPopupData}
          onSave={handleVideoSaved}
          onClose={closeVideoPopup}
        />
      )}

      <PasswordPopup
        isOpen={showPasswordPopup}
        onSuccess={handlePasswordSuccess}
        onClose={() => setShowPasswordPopup(false)}
      />

      <SettingsPopup
        isOpen={showSettingsPopup}
        onClose={() => setShowSettingsPopup(false)}
        onSettingsChange={handleSettingsChange}
        onOpenNewVideo={openNewVideoPopup}
      />
    </div>
  );
}

export default App;